import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LadyBirdDeedSarasota = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Sarasota FL — Avoid Probate | Spanski Law</title>
        <meta name="description" content="Sarasota Lady Bird Deed attorney. Protect your home, avoid probate, keep full control. Venice office serving Sarasota County. Call 941-206-2223 for free consultation." />
        <link rel="canonical" href="https://thelawyerlady.com/lady-bird-deed-sarasota" />
        <meta property="og:title" content="Lady Bird Deed Attorney Sarasota FL — Avoid Probate | Spanski Law" />
        <meta property="og:description" content="Sarasota Lady Bird Deed attorney. Protect your home, avoid probate, keep full control. Venice office serving Sarasota County." />
        <meta property="og:url" content="https://thelawyerlady.com/lady-bird-deed-sarasota" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Lady Bird Deed Florida", "item": "https://thelawyerlady.com/lady-bird-deed-florida" },
            { "@type": "ListItem", "position": 3, "name": "Sarasota", "item": "https://thelawyerlady.com/lady-bird-deed-sarasota" }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          "name": "Season A. Spanski",
          "alternateName": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": { "@type": "PostalAddress", "addressLocality": "Venice", "addressRegion": "FL", "addressCountry": "US" },
          "areaServed": [{ "@type": "City", "name": "Sarasota" }, { "@type": "County", "name": "Sarasota County" }],
          "priceRange": "$800-$1000"
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/lady-bird-deed-florida" className="hover:text-[#c9a87c] transition-colors">Lady Bird Deed Florida</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Sarasota</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Attorney Sarasota FL — Avoid Probate & Protect Your Home
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 mb-8 leading-relaxed">
            I'm Season Spanski, and my office is right here in Venice — just minutes from Sarasota. I help Sarasota County homeowners protect their property using Lady Bird Deeds.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
              <Phone className="w-5 h-5" />
              Call 941-206-2223
            </a>
            <Link to="/#contact" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
              Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <MapPin className="w-4 h-4 text-[#c9a87c]" />
            <span>Venice Office — Serving Sarasota County</span>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6">Lady Bird Deeds for Sarasota County Homeowners</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              If you own a home in Sarasota, North Port, Englewood, or anywhere in Sarasota County, a Lady Bird Deed can help you avoid probate and pass your property directly to your loved ones. As a local attorney with my office in nearby Venice, I understand the needs of homeowners in this area.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A Lady Bird Deed lets you name beneficiaries for your property while keeping full control during your lifetime. You can still sell the house, refinance, or change your mind entirely. When you pass away, the property transfers automatically — no probate required.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Why Sarasota Homeowners Choose Lady Bird Deeds</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Many of my Sarasota clients are retirees who moved here for the weather and want to make things simple for their children back home. Others are longtime residents who've owned their homes for decades and want to protect what they've built.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              A Lady Bird Deed offers a straightforward solution: your home stays in your name, you stay in control, and your family avoids the time and expense of Florida probate when you're gone. For a flat fee of $800-$1,000, it's one of the most affordable ways to protect your largest asset.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Local Service, Personal Attention</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              My Venice office is about 20 minutes south of downtown Sarasota. I'm happy to meet with Sarasota County clients in person, or we can handle everything by phone and video if that's more convenient for you. Either way, you'll work directly with me — not a paralegal or assistant.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I also serve clients throughout Florida, so if you have property in other counties, we can discuss how to handle all of it. Learn more about my <Link to="/lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">statewide Lady Bird Deed services</Link>.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">The Process</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              We start with a consultation where I learn about your property and your goals. I'll draft the Lady Bird Deed, you'll review and approve it, and then we'll arrange for signing and notarization. I handle recording with the Sarasota County Clerk of Court. Most clients have their deed recorded within 1-2 weeks.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Other Florida Locations</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I also serve homeowners in <Link to="/lady-bird-deed-venice-fl" className="text-[#c9a87c] hover:underline">Venice</Link>, <Link to="/lady-bird-deed-tampa-fl" className="text-[#c9a87c] hover:underline">Tampa</Link>, <Link to="/lady-bird-deed-orlando-fl" className="text-[#c9a87c] hover:underline">Orlando</Link>, <Link to="/lady-bird-deed-fort-lauderdale" className="text-[#c9a87c] hover:underline">Fort Lauderdale</Link>, and all 67 Florida counties.
            </p>

          </div>
        </article>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Ready to Get Started?</h2>
            <p className="text-[#faf9f7]/70 mb-8 max-w-2xl mx-auto">
              Call 941-206-2223 or schedule a free consultation online. I'll answer your questions and explain exactly what a Lady Bird Deed can do for you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LadyBirdDeedSarasota;
